import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToday } from '@resource-kit/controllers/react';
import { typeBadgeLabel } from '@features/admin';
import { queryKeys } from '@lib/queryKeys';
import { getMerchantTz } from '@lib/merchant-tz';
import { pendingViewParams, reviewQueueAPI } from '@/services/review-queue';
import { inboxChangeText } from './phoneModel';

const HEADLINE_CLASS = 'm-0 text-[22px] leading-[1.3] font-semibold tracking-[-0.02em]';
const LIST_SIZE = 100;

/**
 * The phone inbox (#2487 I1-I6): today's open messages, the same question as
 * the inbox's Pending tab and the tab-bar count (`pendingViewParams`). Under
 * `queryKeys.reviewQueue.all()`, so resolving a message on the desktop inbox
 * refreshes it. Read-only: handling stays in the desktop inbox.
 */
export function PhoneInbox() {
  const { t, i18n } = useTranslation();
  const params = pendingViewParams(useToday());
  const list = useQuery({
    queryKey: queryKeys.reviewQueue.list({
      view: 'phone',
      status: params.status,
      created_at: params.filters['created_at'],
      size: LIST_SIZE,
    }),
    queryFn: () => reviewQueueAPI.list({ ...params, page: 1, size: LIST_SIZE }),
    staleTime: 5_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const clock = new Intl.DateTimeFormat(i18n.language, {
    timeZone: getMerchantTz(),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  let body = null;
  if (list.isError) {
    body = <p className="text-[13px] text-destructive">{t('dispatch.phone.inbox.loadError')}</p>;
  } else if (list.data) {
    const { items, total } = list.data;
    body = (
      <>
        <h2 className={HEADLINE_CLASS}>
          {total > 0
            ? t('dispatch.phone.inbox.title', { count: total })
            : t('dispatch.phone.inbox.empty')}
        </h2>
        {items.length > 0 && (
          <div className="rounded-xl bg-card outline outline-1 -outline-offset-1 outline-border/50">
            {items.map((item) => {
              const label = typeBadgeLabel(item, t);
              const changes = inboxChangeText(item).map((key) => t(key));
              // Design copy: `{label} · {HH:mm} {change}`, time and change one phrase.
              const time = clock.format(new Date(item.created_at));
              const sub = [
                label,
                changes.length > 0
                  ? `${time} ${changes.join(t('dispatch.phone.inbox.changeJoin'))}`
                  : time,
              ];
              return (
                <div
                  key={item.id}
                  data-testid={`phone-inbox-item-${item.id}`}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-1 border-t border-border/50 px-3.5 py-3 first:border-t-0"
                >
                  <span className="min-w-0">
                    <span className="block font-mono text-[12.5px]">{item.order_name ?? '—'}</span>
                    <span className="block text-[12.5px] text-muted-foreground">
                      {sub.join(' · ')}
                    </span>
                  </span>
                  <span className="rounded-full bg-warning/12 px-2 text-[12px] leading-[22px] font-medium whitespace-nowrap text-warning">
                    {label}
                  </span>
                  {item.order_id !== null && (
                    <Link
                      to={`/orders/${item.order_id}`}
                      className="col-span-2 inline-flex h-10 items-center justify-center rounded-lg border border-border bg-card font-medium text-foreground no-underline"
                    >
                      {t('dispatch.phone.inbox.openOrder')}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <div data-testid="phone-inbox" className="flex flex-col gap-3">
      {body}
    </div>
  );
}
